/* The SYSTEM FAILURES block — the visibility line's interior.
 *
 * These rows are machine failures. They are NOT officer queue rows:
 * they never enter the J/K walk, never open the detail panel, and never
 * count toward an officer total. But they are always visible, because
 * hiding them would rebuild the silent dead end one level up — and the
 * visibility line is itself a failure if it cannot be seen.
 *
 * The split runs through the VERB, not across the task (Ale 2026-08-09):
 * whole-group RETRY stays with the officer — one click, no machine
 * knowledge needed, and the officer is the one who notices their record
 * is missing a document's facts. Admin keeps diagnosis, PARTIAL retry
 * (choosing the poison leg), code fixes, and the `unrouted` kind, which
 * carries no officer verb at all.
 *
 * Honest reporting: a retry that dead-letters again SAYS SO on the row.
 * There is no silent requeue.
 */

import { useState } from 'react'
import type { TaskQueueItem } from '../../lib/tasks'
import { retryFailure, retryOutcomeText, type StuckActionResult } from './api'
import { useAuth } from '../../contexts/useAuth'
import { ADMIN_KINDS, ADMIN_VIEW_ROUTE, officerVerbFor, placementFor } from './placement'
import { ageDays, ageLabel, AGE_WARN_DAYS } from './queueUtils'

function kindLabel(taskType: string): string {
  const kind = placementFor(taskType).adminKind
  return ADMIN_KINDS.find((k) => k.key === kind)?.label ?? 'unrouted'
}

export default function SystemFailures({
  tasks,
  onChanged,
}: {
  tasks: TaskQueueItem[]
  onChanged?: () => void
}) {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const [busy, setBusy] = useState<string | null>(null)
  const [outcomes, setOutcomes] = useState<Record<string, StuckActionResult>>({})

  async function retry(task: TaskQueueItem) {
    setBusy(task.task_id)
    try {
      const res = await retryFailure(task.task_id)
      setOutcomes((prev) => ({ ...prev, [task.task_id]: res }))
      // Only a landing changes the queue; a re-dead-letter leaves the
      // row exactly where it is, still saying what happened.
      if (res.outcome !== 'dead_lettered_again') onChanged?.()
    } catch (err) {
      setOutcomes((prev) => ({
        ...prev,
        [task.task_id]: {
          task_id: task.task_id,
          outcome: err instanceof Error ? err.message : 'the retry could not be sent',
        },
      }))
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="sysfail" data-testid="wb-system-failures">
      <div className="sysfailhead">
        <h2>
          {tasks.length} system failure{tasks.length === 1 ? '' : 's'}{' '}
          <span className="adminmark">admin</span>
        </h2>
        {/* The diagnosis surface is role-gated to admin; an officer's
            click could only bounce (Today), so the link renders for
            admins alone — the never-dead-end rule (2026-07-22). */}
        {isAdmin ? (
          <a className="adminlink" href={ADMIN_VIEW_ROUTE} data-testid="wb-admin-link">
            Open the admin view →
          </a>
        ) : null}
      </div>
      <p className="sub">
        The machine failed here, not you. Retry re-runs the whole group in one click; anything finer
        — diagnosis, severing a single leg, a code fix — is admin&rsquo;s.
      </p>
      {tasks.length === 0 ? (
        <div className="sysrow empty" data-testid="wb-system-failures-empty">
          Nothing has failed.
        </div>
      ) : null}
      {tasks.map((t) => {
        const verb = officerVerbFor(t.task_type)
        const days = ageDays(t.created_at) ?? 0
        const outcome = outcomes[t.task_id]
        return (
          <div
            className="sysrow"
            data-testid="wb-system-failure-row"
            data-task-type={t.task_type}
            data-task-id={t.task_id}
            key={t.task_id}
          >
            <span className="syskind">{kindLabel(t.task_type)}</span>
            <span className="systitle" title={t.title}>
              {t.title}
            </span>
            <span className={`qage${days > AGE_WARN_DAYS ? ' warn' : ''}`}>
              {ageLabel(t.created_at)}
            </span>
            {verb === 'retry' ? (
              <button
                type="button"
                className="btn"
                data-testid="wb-retry"
                disabled={busy === t.task_id}
                onClick={() => retry(t)}
              >
                {busy === t.task_id ? 'Retrying…' : 'Retry'}
              </button>
            ) : (
              <span className="adminonly" data-testid="wb-admin-only">
                admin only
              </span>
            )}
            {outcome ? (
              <span
                className={`sysoutcome${outcome.outcome === 'dead_lettered_again' ? ' bad' : ''}`}
                data-testid="wb-retry-outcome"
              >
                {retryOutcomeText(outcome)}
              </span>
            ) : null}
          </div>
        )
      })}
    </section>
  )
}
